"use client"

import { useState } from "react"
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from "@mui/material"
import { ContentCopy } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { resetUserPassword } from "../../services/user.service"

const MIN_PASSWORD_LENGTH = 6

const ResetPasswordDialog = ({ open, user, onClose }) => {
  const { showNotification } = useNotification()
  const [mode, setMode] = useState("generate")
  const [newPassword, setNewPassword] = useState("")
  const [temporaryPassword, setTemporaryPassword] = useState("")
  const [saving, setSaving] = useState(false)

  const handleClose = () => {
    setMode("generate")
    setNewPassword("")
    setTemporaryPassword("")
    onClose()
  }

  const handleReset = async () => {
    setSaving(true)
    try {
      const response = await resetUserPassword(user._id, mode === "set" ? newPassword : undefined)
      if (response.data.temporaryPassword) {
        setTemporaryPassword(response.data.temporaryPassword)
      } else {
        showNotification("Password updated", "success")
        handleClose()
      }
    } catch (error) {
      showNotification(error.message || "Failed to reset password", "error")
    } finally {
      setSaving(false)
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(temporaryPassword)
      showNotification("Copied", "success")
    } catch (error) {
      showNotification("Could not copy, select the password and copy it manually", "error")
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Reset password</DialogTitle>
      <DialogContent>
        {temporaryPassword ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This is shown only once. Share it securely with {user.firstName} and ask them to change it after signing in.
            </Alert>
            <TextField
              fullWidth
              label="Temporary password"
              value={temporaryPassword}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy">
                      <IconButton aria-label="Copy password" onClick={handleCopy} edge="end">
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </>
        ) : (
          <>
            <RadioGroup value={mode} onChange={(event) => setMode(event.target.value)}>
              <FormControlLabel value="generate" control={<Radio />} label="Generate a temporary password" />
              <FormControlLabel value="set" control={<Radio />} label="Set a password myself" />
            </RadioGroup>
            {mode === "set" && (
              <TextField
                fullWidth
                autoFocus
                type="password"
                label="New password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {temporaryPassword ? (
          <Button onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              onClick={handleReset}
              variant="contained"
              disabled={saving || (mode === "set" && newPassword.length < MIN_PASSWORD_LENGTH)}
            >
              Reset password
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ResetPasswordDialog
